import { useEffect, useState } from 'react';
import { ErrorApi } from '../../../api/problema';
import type { CanalEntrega, Cliente, Entrega, ListaEntregas } from '../../../api/ops/clientes';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { Ventana } from '../../../componentes/Ventana';
import { estadoDeError } from '../../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import type { ContextoClientes } from '../lista/contexto';
import { CANALES, ESTADO_ENTREGA, PERMISOS, diaYHora, textoDeError } from '../lista/textos';

/** Se puede reenviar lo que ya salió (confirmaciones y pases, no las campañas); manda `resendable` de la API si llega. */
const REENVIABLE = new Set(['sent', 'delivered', 'read', 'fallback', 'failed']);

function motivo(e: Entrega): string | null {
    if (e.status === 'fallback') {
        return 'El WhatsApp no llegó y se envió por email.';
    }
    switch (e.discard_reason) {
        case 'no_contact':
            return 'No tiene teléfono ni email.';
        case 'no_marketing_consent':
            return `Sin permiso comercial${e.final_channel == null ? '' : ` por ${e.final_channel === 'email' ? 'email' : CANALES[e.final_channel].nombre}`}.`;
        case 'season_pause':
            return 'En pausa de temporada.';
        case 'marketing_quota_exhausted':
            return 'Sin mensajes comerciales disponibles en tu plan.';
        case 'profile_anonymized':
            return 'La ficha está anonimizada.';
        default:
            return null;
    }
}

/**
 * Entregas (2.3): los mensajes que le ha enviado el negocio, por qué canal y si le llegaron (`listDeliveries` de Entregas),
 * con «Reenviar», que deja elegir el canal entre los que tiene (`resendDelivery` con `channel`). Avisa al cliente: sin
 * «Deshacer» (G159). Mientras el módulo de Entregas no exista, la API responde 404: se ve vacía, sin error (como la campana).
 */
export function Entregas({ c, cliente }: { c: ContextoClientes; cliente: Cliente }) {
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<ListaEntregas | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [reenviar, setReenviar] = useState<Entrega | null>(null);
    const [sinPermiso, setSinPermiso] = useState(false);
    const { ops, negocio, permisos } = c;
    const puedeReenviar = permisos.puede(PERMISOS.reenviar) && !c.soloLectura;

    useEffect(() => {
        let vivo = true;
        ops.listDeliveries(negocio, { customer_id: cliente.id, cursor: pag.cursor ?? undefined })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (!vivo) {
                    return;
                }
                // Entregas aún no existe (404): sin entregas, sin error.
                if (e instanceof ErrorApi && e.estado === 404) {
                    setLista({ data: [], next_cursor: null });
                    setError(null);
                    return;
                }
                if (e instanceof ErrorApi && e.estado === 403) {
                    setSinPermiso(true);
                    setLista({ data: [], next_cursor: null });
                    return;
                }
                setError(e);
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, cliente.id, pag.cursor, intento]);

    if (error !== null && lista === null) {
        return <EstadoError seccion="las entregas" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={c.movil} />;
    }
    if (lista === null) {
        return null;
    }

    return (
        <div className="rv-cl-seccion">
            {lista.data.length === 0 ? (
                <p className="rv-cl-vacio">{sinPermiso ? 'Con tu rol no puedes ver sus entregas.' : 'Todavía no le has enviado nada.'}</p>
            ) : (
                <div className="rv-cl-lineas">
                    {lista.data.map((e) => {
                        const [texto, chip] = ESTADO_ENTREGA[e.status];
                        const canal = e.final_channel ?? null;
                        const linea = [canal === null ? null : CANALES[canal].nombre, e.destination_hint ?? null, diaYHora(e.created_at)].filter((x): x is string => x !== null && x !== '').join(' • ');
                        const porque = motivo(e);
                        return (
                            <div key={e.id} className="rv-cl-entrada">
                                <span className="rv-cl-entrada__baldosa">
                                    <Icono nombre={canal === null ? 'send' : CANALES[canal].icono} />
                                </span>
                                <span className="rv-cl-linea">
                                    <strong>{e.deliverable_label ?? e.message_type}</strong>
                                    <span>{linea}</span>
                                    {porque !== null && <span className="rv-cl-linea__motivo">{porque}</span>}
                                </span>
                                <span className="rv-cl-entrada__lado">
                                    <Chip estado={chip}>{texto}</Chip>
                                    {puedeReenviar && e.purpose === 'service' && (e.resendable ?? REENVIABLE.has(e.status)) && (
                                        <Boton
                                            tipo="secundario"
                                            icono="send"
                                            movil={c.movil}
                                            onClick={() => {
                                                setReenviar(e);
                                            }}
                                        >
                                            Reenviar
                                        </Boton>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            {(pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={c.movil} />
            )}
            <p className="rv-cl-nota">El teléfono y el email se ven ocultos en parte. El detalle se guarda 12 meses.</p>
            {reenviar !== null && (
                <VentanaReenviar
                    c={c}
                    cliente={cliente}
                    entrega={reenviar}
                    onCerrar={() => {
                        setReenviar(null);
                    }}
                    onHecho={() => {
                        setReenviar(null);
                        pag.reiniciar();
                        setIntento((i) => i + 1);
                    }}
                />
            )}
        </div>
    );
}

function VentanaReenviar({ c, cliente, entrega, onCerrar, onHecho }: { c: ContextoClientes; cliente: Cliente; entrega: Entrega; onCerrar: () => void; onHecho: () => void }) {
    const telefono = cliente.account?.phone ?? cliente.local.phone ?? null;
    const email = cliente.account?.email ?? cliente.local.email ?? null;
    const baja = (k: CanalEntrega) => (cliente.consents ?? []).some((p) => p.channel === k && p.purpose === 'service' && p.state === 'withdrawn');
    const canales: CanalEntrega[] = [...(telefono !== null && !baja('whatsapp') ? (['whatsapp'] as const) : []), ...(email !== null && !baja('email') ? (['email'] as const) : [])];
    const inicial = entrega.final_channel != null && canales.includes(entrega.final_channel) ? entrega.final_channel : (canales[0] ?? null);
    const [canal, setCanal] = useState<CanalEntrega | null>(inicial);
    const [ocupado, setOcupado] = useState(false);
    const titulo = entrega.deliverable_label ?? entrega.message_type;
    const destino = canal === 'email' ? (email ?? '') : canal === 'whatsapp' ? (telefono ?? '') : '';

    const enviar = () => {
        setOcupado(true);
        c.ops
            .resendDelivery(c.negocio, entrega.id, canal)
            .then(() => {
                c.avisar({ tipo: 'ok', texto: `Reenviado${canal === null ? '' : ` por ${CANALES[canal].nombre}`}. Aquí verás si le llega.` });
                onHecho();
            })
            .catch((e: unknown) => {
                c.avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido reenviarlo. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <Ventana
            abierta
            titulo={`Reenviar: ${titulo.charAt(0).toLocaleLowerCase('es')}${titulo.slice(1)}`}
            texto={canal === null ? 'Se envía otra vez por su canal preferido. Queda como una entrega nueva.' : `Se envía otra vez por ${CANALES[canal].nombre} a ${destino}. Queda como una entrega nueva.`}
            accion="Reenviar"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Reenviando…"
            movil={c.movil}
            onAccion={enviar}
            onCerrar={onCerrar}
        >
            {canales.length > 1 && (
                <div className="rv-cl-eleccion">
                    <span className="rv-cl-eleccion__titulo">Por dónde</span>
                    <div role="radiogroup" aria-label="Por dónde" className="rv-cl-eleccion__opciones">
                        {canales.map((k) => (
                            <button
                                key={k}
                                type="button"
                                role="radio"
                                aria-checked={canal === k}
                                className="rv-cl-eleccion__b"
                                onClick={() => {
                                    setCanal(k);
                                }}
                            >
                                <Icono nombre={CANALES[k].icono} />
                                {CANALES[k].nombre}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </Ventana>
    );
}
